import React, { useEffect, useState, useRef } from 'react';
import { Feature } from 'ol';
import { VectorLayer } from './Layer.js';
import Polygon from 'ol/geom/Polygon';
import { outlineExtent } from './MapVectorStyles.js';
import LineString from 'ol/geom/LineString';
import { createXYZ } from 'ol/tilegrid';
import OLVectorSource from 'ol/source/Vector';
import { getSimpleGeometry } from '../controller/LayerQueries';

const distanceBetweenPoints = (coord1, coord2) => {
    var line = new LineString([coord1, coord2]);
    return Math.round(line.getLength() * 100) / 100;
};
const drawExtentPolygon = (ext, _offset = 0) => {
    const diagonalDistance = distanceBetweenPoints(ext.slice(0, 2), ext.slice(2));

    const offset = diagonalDistance * (_offset / 100);

    const minx = ext[0] + offset;
    const miny = ext[1] + offset * 2;
    const maxx = ext[2] - offset;
    const maxy = ext[3] - offset;

    const topLeft = [minx, maxy];
    const bottomLeft = [minx, miny];
    const topRight = [maxx, maxy];
    const bottomRight = [maxx, miny];

    const coords = [topLeft, topRight, bottomRight, bottomLeft, topLeft, bottomRight, topRight, bottomLeft];

    const feature = new Feature({
        geometry: new Polygon([coords])
    });
    return feature;
};

const DrawExtent = (props) => {
    const { map, extent, opacity = 1, visible = true, zIndex = 0, title = 'extent border', style = outlineExtent } = props;
    const [features, setFeatures] = useState([]);

    const redrawFeature = () => {
        const ext = extent || map.getView().calculateExtent(map.getSize());
        const feature = drawExtentPolygon(ext, 2);
        setFeatures([feature]);
    };
    useEffect(() => map && redrawFeature(), [map, extent]);

    return (
        <VectorLayer
            style={style}
            features={features}
            title={title}
            zIndex={zIndex}
            opacity={opacity}
            visible={visible}
            onAdded={(lay) => {
                redrawFeature(lay);
            }}
            map={map}
        />
    );
};
export { DrawExtent };

const getTilesInView = (args) => {
    const { zoom, extent } = args;
    const simpleZoom = Math.max(Math.floor(zoom), 6); //min zoom
    const grid = createXYZ();
    const tilesObj = {};
    grid.forEachTileCoord(extent, simpleZoom, (coord) => {
        let tileExtent = grid.getTileCoordExtent(coord);
        tilesObj[`z:${coord[0]}x:${coord[1]}y:${coord[2]}`] = tileExtent;
    });
    return tilesObj;
};

export { getTilesInView };

const TileTracker = (props) => {
    const { map, onChange = () => {}, minZoom = 0, limits = {} } = props;
    const [tiles, setTiles] = useState([]);
    const grid = useRef(createXYZ());

    //ΔVΔVΔ: If rule has a num > x condition and meets the condition

    const validateRule = (num, rule, p = 0) => {
        num = num * 1;
        if (rule.indexOf('-') === -1) return num === rule * 1; //not a range just number
        let range = rule.split('-').map((n) => parseInt(n));
        let min = range[0] - p;
        let max = range[1] + p;
        if (min < num < max) return true;
        return false;
    };
    const withinLimit = (coord) => {
        let cz = coord[0];
        let cx = coord[1];
        let cy = coord[2];
        if (!cz in limits) return true; //no rule present
        var rules = limits[cz];
        let xRules = rules.x.split(',');
        let yRules = rules.y.split(',');
        if (xRules.length !== yRules.length) {
            console.error(`Invaid zoom rule [${rules.x}] and [${rules.y}] do not have matching lengths`);
            return true; //Ignore rule
        }
        for (let i = 0; i < xRules.length; i++) {
            let xMatch = validateRule(cx, xRules[i], rules.p);
            let yMatch = validateRule(cy, yRules[i], rules.p);
            // console.log('XXX', cx, xRules[i], xMatch);
            // console.log('YYY', cy, yRules[i], yMatch);
            if (xMatch && yMatch) return true;
        }
        return false;
    };

    const tilesViewed = () => {
        let extent = map.getView().calculateExtent(map.getSize());
        let zoom = Math.max(map.getView().getZoom(), minZoom); //minimum zoom 6 for fetching poi data
        // let zoom = map.getView().getZoom();

        const tilesObj = {};
        grid.current.forEachTileCoord(extent, zoom, (coord) => {
            // if (withinLimit(coord)) {
            let tileExtent = grid.current.getTileCoordExtent(coord);
            tilesObj[`z:${coord[0]}x:${coord[1]}y:${coord[2]}`] = tileExtent;
            // }
        });
        // console.log(tilesObj);
        setTiles(tilesObj);
    };

    useEffect(() => {
        tilesViewed();
        map.on('moveend', async () => tilesViewed());
    }, []);

    useEffect(() => tilesViewed(), [map]);
    useEffect(() => onChange(tiles), [tiles]);
    return null;
};
export { TileTracker };

const FipsTracker = (props) => {
    const { map, year, onChange, onSourceInit } = props;
    const [fips, setFips] = useState(new Set());
    const [source, setSource] = useState(null);

    const updateSource = async () => {
        const nationalCountyFeatures = await getSimpleGeometry({ scope: 'national', tract: 'county', year: year.label });

        const lyrSource = new OLVectorSource({
            features: nationalCountyFeatures,
            overlaps: false
        });
        setSource(lyrSource);
    };

    useEffect(() => {
        if (!map || !source) return;
        updateFips();
        map.on('moveend', async () => updateFips());
    }, [source, map]);

    useEffect(() => onSourceInit && onSourceInit(source), [source]);
    useEffect(async () => year && updateSource(), [map, year]);
    useEffect(() => map && onChange(fips), [fips]);

    const updateFips = (detail = 'low') => {
        const obj = new Set();
        if (!map || !source) return obj;

        source.forEachFeatureIntersectingExtent(map.getView().calculateExtent(map.getSize()), function (feature) {
            let fip = feature.get('FIPS') || feature.get('STATEFP') + feature.get('COUNTYFP');
            fip = detail === 'low' ? fip.toString().substring(0, 2) : fip.toString();
            obj.add(fip);
        });
        setFips(obj);
    };

    return null;
};
export { FipsTracker };
